import { Ref } from 'vue';
import { useStorage } from 'vue3-storage';
import axios from 'axios';
import { each } from 'lodash-es';
import qs from 'qs';
import { Endpoint, ApiRequestParams } from '@/types/api.type.ts';
import { PageProperties } from '@/types/page-properties.type.ts';
import useNotifications from '@/utils/useNotifications.ts';
import { convertMapToFlat } from '@/utils/collection-helpers.ts';

const $storage = useStorage();

class Api {
    private axios: typeof axios = axios;
    private endpoints: Endpoint[] = [];
    private $PageProperties: Ref<PageProperties>;

    constructor($PageProperties: Ref<PageProperties>) {
        this.$PageProperties = $PageProperties;

        this.axios.defaults.headers.common.Accept = 'application/json';
        this.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
        this.axios.defaults.headers.common['Accept-Language'] = $storage.getStorageSync('locale');

        const token = $storage.getStorageSync('token');
        if (token) {
            this.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
        }
    }

    public setToken(token?: string): void {
        this.axios.defaults.headers.common.Authorization = 'Bearer ' + token;
    }

    public async callNew(
        endpointName: string,
        params: ApiRequestParams | null = null,
        headers: any = null,
        abortController: AbortController | null = null,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            const endpoint = this.getEndpointByName(endpointName);

            let url = this.getApiPrefixNew() + endpoint.path;

            const segments = params?.segments || {};
            each(segments, (value: string, key: string) => {
                url = url.replace(':' + key, value);
            });

            // объект для запроса через axios
            // noinspection JSUnusedGlobalSymbols
            const requestParams = {
                method: endpoint.method,
                url: url,
                params: params?.query || {},
                data: params?.requestBody,
                paramsSerializer: (params: any): string => qs.stringify(params, { arrayFormat: 'brackets' }),
                signal: abortController?.signal,
                ...headers,
            };

            // // вставляем переданные заголовки
            // if (headers !== null) {
            //     _.merge(requestParams, headers);
            // }

            this.axios
                .request(requestParams)
                .then((response: any) => {
                    resolve(response);
                })
                .catch((response: any) => {
                    if (this.axios.isCancel(response)) {
                        reject(response);
                    }
                    else {
                        const { showError } = useNotifications();

                        if (response.response.status === 401) {
                            $storage.clearStorageSync();

                            if (endpoint.name !== 'login') {
                                window.location.replace('/auth/login?redirect=' + window.location.pathname);
                            }
                            else {
                                showError({
                                    message: response.response.data.message,
                                });
                            }
                        }

                        if (response.response.status === 403) {
                            this.$PageProperties.value.accessDenied = true;
                        }

                        // сомнительно. С бэка может вернуться 404 ошибка не на сущность, а на что-то отсутствующее в бизнес-логике
                        // если только добавить условие, что запрос с типом GET
                        if (response.response.status === 404) {
                            this.$PageProperties.value.notFound = true;
                        }

                        if (response.response.status === 422) {
                            showError({
                                errors: convertMapToFlat<string>(response.response.data.errors || {}),
                                maxErrors: 8,
                            });
                        }

                        if (response.response.status === 500) {
                            if (response.response.request.responseType === 'blob') {
                                response.response.data.text()
                                    .then((text: string) => {
                                        const data = JSON.parse(text);
                                        showError({
                                            message: data.message,
                                        });
                                    });
                            }
                            else {
                                showError({
                                    message: response.response.data.message,
                                });
                            }
                        }

                        if (response.response.status === 404) {
                            showError({
                                message: 'Не найдено',
                            });
                        }

                        reject(response);
                    }
                });
        });
    }

    public getEndpointByName(endpointName: string): Endpoint {
        const endpoints = this.endpoints.filter((endpoint: Endpoint) => {
            return endpoint.name === endpointName;
        });

        if (endpoints[0] === undefined) {
            throw new Error(`Endpoint "${endpointName}" not found`);
        }

        return endpoints[0];
    }

    public addEndpoints(endpoints: Endpoint[]): void {
        this.endpoints.push(...endpoints);
    }

    private getApiHost(): string {
        if (this.getEnv('VITE_APP_BACKEND_SCHEME') && this.getEnv('VITE_APP_BACKEND_HOST')) {
            return this.getEnv('VITE_APP_BACKEND_SCHEME') + '://' + this.getEnv('VITE_APP_BACKEND_HOST');
        }

        return 'http://localhost:3001';
    }

    public getApiPrefixNew(): string {
        try {
            return this.getApiHost() + '/api';
        }
        catch (err: any) {
            console.error(err.message);

            throw err;
        }
    }

    public getEnv(name: 'VITE_APP_BACKEND_SCHEME' | 'VITE_APP_BACKEND_HOST' | 'VITE_APP_REVERB_APP_KEY'): string | undefined {
        if (typeof window.configs !== 'undefined' && typeof window.configs[name] !== 'undefined') {
            return window.configs[name];
        }

        if (typeof import.meta.env[name] !== 'undefined') {
            return import.meta.env[name];
        }

        return undefined;
    }
}

export default Api;
